/**
 * Structure Proposal Store — Layer 1 (document proposals).
 *
 * Flow: generate → poll until complete → user edits locally (include
 * toggles, renames) → accept (creates artifacts) | discard.
 * Spec: Specs/response-structure-proposal-spec.md §2.
 */

import { create } from 'zustand'
import {
  startStructureProposal,
  getStructureProposal,
  acceptStructureProposal,
  discardStructureProposal,
  type StructureGeneration,
  type ProposedDocument,
} from '@/lib/api'

const POLL_INTERVAL_MS = 2000
/**
 * The first poll lands early: an empty bid short-circuits server-side in tens
 * of milliseconds, so a full interval of spinner reads as "nothing happened".
 */
const FIRST_POLL_DELAY_MS = 500
const MAX_POLL_ATTEMPTS = 60

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** A proposed document with local review edits. */
export interface EditableDocument extends ProposedDocument {
  included: boolean
}

interface StructureProposalState {
  generation: StructureGeneration | null
  /** Local, editable copy of the proposal documents (review state). */
  documents: EditableDocument[]
  isGenerating: boolean
  isLoading: boolean
  isAccepting: boolean
  error: string | null
  /**
   * True once the user has edited the held proposal (renames, include
   * toggles). Regenerating throws those edits away, so the UI confirms first.
   * Cleared whenever the proposal is replaced or resolved.
   */
  isDirty: boolean

  /** Start a new generation and poll to completion. */
  generate: (bidId: string) => Promise<void>
  /** Load the latest generation (page mount / return visits). */
  load: (bidId: string) => Promise<void>
  setIncluded: (key: string, included: boolean) => void
  updateDocument: (key: string, patch: Partial<Pick<EditableDocument, 'name' | 'description'>>) => void
  /** Accept the included documents → creates artifacts. Returns created ids. */
  accept: (bidId: string) => Promise<string[] | null>
  discard: (bidId: string) => Promise<void>
  reset: () => void
}

/**
 * Bumped by every poll start and by reset(). A loop whose epoch is stale stops
 * and never writes, so a superseded generation can't clobber the live one.
 */
let pollEpoch = 0

function toEditable(generation: StructureGeneration | null): EditableDocument[] {
  if (!generation?.proposal) return []
  return generation.proposal.proposedDocuments.map((d) => ({ ...d, included: true }))
}

function isTerminal(status: StructureGeneration['status']): boolean {
  return status !== 'pending' && status !== 'running'
}

/**
 * State for a generation that has stopped moving. Every terminal outcome is
 * user-visible: documents to review, a failure with its reason, or a complete
 * generation with zero documents whose `proposal.notes` explain why.
 */
function terminalPatch(generation: StructureGeneration): Partial<StructureProposalState> {
  if (generation.status === 'failed') {
    return {
      generation,
      documents: [],
      isGenerating: false,
      error: generation.error ?? 'Proposal generation failed',
      isDirty: false,
    }
  }
  return {
    generation,
    documents: generation.status === 'complete' ? toEditable(generation) : [],
    isGenerating: false,
    error: null,
    isDirty: false,
  }
}

/** Poll the latest generation until it reaches a terminal state. */
async function pollUntilTerminal(
  bidId: string,
  set: (patch: Partial<StructureProposalState>) => void
): Promise<void> {
  const epoch = ++pollEpoch
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await sleep(attempt === 0 ? FIRST_POLL_DELAY_MS : POLL_INTERVAL_MS)
    if (epoch !== pollEpoch) return
    const generation = await getStructureProposal(bidId).catch(() => null)
    if (epoch !== pollEpoch) return
    if (generation && isTerminal(generation.status)) {
      set(terminalPatch(generation))
      return
    }
  }
  set({ isGenerating: false, error: 'Proposal generation timed out — try again' })
}

export const useStructureProposalStore = create<StructureProposalState>((set, get) => ({
  generation: null,
  documents: [],
  isGenerating: false,
  isLoading: false,
  isAccepting: false,
  error: null,
  isDirty: false,

  generate: async (bidId) => {
    // In-flight guard: without it, an impatient repeat click dispatched a
    // second worker that superseded the first (F5).
    if (get().isGenerating) return
    set({ isGenerating: true, error: null, generation: null, documents: [], isDirty: false })
    try {
      await startStructureProposal(bidId)
    } catch (err) {
      set({ isGenerating: false, error: (err as Error).message })
      return
    }
    await pollUntilTerminal(bidId, set)
  },

  load: async (bidId) => {
    set({ isLoading: true, error: null })
    let generation: StructureGeneration | null
    try {
      generation = await getStructureProposal(bidId)
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message })
      return
    }

    if (!generation) {
      set({ generation: null, documents: [], isLoading: false, isGenerating: false, isDirty: false })
      return
    }

    if (isTerminal(generation.status)) {
      set({ isLoading: false, ...terminalPatch(generation) })
      return
    }

    // Still running server-side (e.g. page reload mid-generation) — resume.
    set({ generation, documents: [], isLoading: false, isGenerating: true, isDirty: false })
    await pollUntilTerminal(bidId, set)
  },

  setIncluded: (key, included) => {
    set({
      documents: get().documents.map((d) => (d.key === key ? { ...d, included } : d)),
      isDirty: true,
    })
  },

  updateDocument: (key, patch) => {
    set({
      documents: get().documents.map((d) => (d.key === key ? { ...d, ...patch } : d)),
      isDirty: true,
    })
  },

  accept: async (bidId) => {
    const { generation, documents } = get()
    if (!generation) return null
    const included = documents.filter((d) => d.included)
    if (included.length === 0) {
      set({ error: 'Select at least one document to create' })
      return null
    }
    set({ isAccepting: true, error: null })
    try {
      const { artifactIds } = await acceptStructureProposal(
        bidId,
        generation.id,
        included.map(({ included: _included, rationale: _rationale, ...doc }) => doc)
      )
      set({ isAccepting: false, generation: null, documents: [], isDirty: false })
      return artifactIds
    } catch (err) {
      set({ isAccepting: false, error: (err as Error).message })
      return null
    }
  },

  discard: async (bidId) => {
    const { generation } = get()
    if (!generation) return
    try {
      await discardStructureProposal(bidId, generation.id)
    } catch {
      // Discard is best-effort; clearing local state is what the user sees.
    }
    set({ generation: null, documents: [], error: null, isDirty: false })
  },

  reset: () => {
    // Orphan any in-flight poll loop so it can't write into the cleared store.
    pollEpoch++
    set({
      generation: null,
      documents: [],
      isGenerating: false,
      isLoading: false,
      isAccepting: false,
      error: null,
      isDirty: false,
    })
  },
}))
